import { useQuery } from '@tanstack/react-query';

import axiosInstance from './axios';
import { getConfig, STATIC_OPTIONS } from './config';
import { APIResponse, BillingType, GymType, LocationType, PaymentStatus, PlanStatus, PlanType, Role } from './types';
import { MemberStatus } from '~/modules/@shared/constants/userConstants';

const getBillingTypes = () => {
  const config = getConfig();
  if (STATIC_OPTIONS) {
    return import('~/assets/data/api/v1/plans/billing_types.json').then(({ data }: any) => data);
  } else {
    return axiosInstance
      .get<APIResponse<{ id: number; name: BillingType }[]>>(`${config.domains.plans}/plans/billing_types`)
      .then(({ data }) => data?.data);
  }
};
export const getBillingTypesQuery = () => ({ queryKey: ['billing-types'], queryFn: getBillingTypes });
export const useGetBillingTypesQuery: any = () => useQuery(getBillingTypesQuery());

const getPlanTypes = () => {
  const config = getConfig();
  if (STATIC_OPTIONS) {
    return import('~/assets/data/api/v1/plans/plan_types.json').then(({ data }: any) => data);
  } else {
    return axiosInstance
      .get<APIResponse<{ id: number; name: PlanType }[]>>(`${config.domains.plans}/plans/plan_types`)
      .then(({ data }) => data?.data);
  }
};
export const useGetPlanTypesQuery = () => useQuery({ queryKey: ['plan-types'], queryFn: getPlanTypes });

const getPlanStatusTypes = () => {
  const config = getConfig();
  if (STATIC_OPTIONS) {
    return import('~/assets/data/api/v1/plans/plan_status_types.json').then(({ data }: any) => data);
  } else {
    return axiosInstance
      .get<APIResponse<{ id: number; name: PlanStatus }[]>>(`${config.domains.plans}/plans/plan_status_types`)
      .then(({ data }) => data?.data);
  }
};
export const useGetPlanStatusTypesQuery: any = () =>
  useQuery({ queryKey: ['plan-status-types'], queryFn: getPlanStatusTypes });

const getDurationTypes = () => {
  const config = getConfig();
  if (STATIC_OPTIONS) {
    return import('~/assets/data/api/v1/plans/duration_types.json').then(({ data }: any) => data);
  } else {
    return axiosInstance
      .get<APIResponse<{ id: number; name: string }[]>>(`${config.domains.plans}/plans/duration_types`)
      .then(({ data }) => data?.data);
  }
};
export const useGetDurationTypesQuery = () => useQuery({ queryKey: ['duration-types'], queryFn: getDurationTypes });

const getGenderTypes = () => {
  const config = getConfig();

  if (STATIC_OPTIONS) {
    return import('~/assets/data/api/v1/members/gender_types.json').then(({ data }: any) => data);
  } else {
    return axiosInstance
      .get<APIResponse<{ id: number; name: string }[]>>(`${config.domains.members}/members/gender_types`)
      .then(({ data }) => data?.data);
  }
};
export const useGetGenderTypesQuery: any = () => useQuery({ queryKey: ['gender-types'], queryFn: getGenderTypes });

const getMemberStatusTypes = () => {
  const config = getConfig();
  if (STATIC_OPTIONS) {
    return import('~/assets/data/api/v1/members/member_status_types.json').then(({ data }: any) => data);
  } else {
    return axiosInstance
      .get<APIResponse<{ id: number; name: MemberStatus }[]>>(`${config.domains.members}/members/member_status_types`)
      .then(({ data }) => data?.data);
  }
};
export const useGetMemberStatusTypesQuery: any = () =>
  useQuery({ queryKey: ['member-status-types'], queryFn: getMemberStatusTypes });

const getObjectiveTypes = () => {
  const config = getConfig();

  if (STATIC_OPTIONS) {
    return import('~/assets/data/api/v1/members/objective_types.json').then(({ data }: any) => data);
  } else {
    return axiosInstance
      .get<APIResponse<{ id: number; name: string }[]>>(`${config.domains.members}/members/objective_types`)
      .then(({ data }) => data?.data);
  }
};
export const useGetObjectiveTypesQuery: any = () =>
  useQuery({ queryKey: ['objective-types'], queryFn: getObjectiveTypes });

const getPaymentStatusTypes = () => {
  const config = getConfig();

  if (STATIC_OPTIONS) {
    return import('~/assets/data/api/v1/members/payment_status_types.json').then(({ data }: any) => data);
  } else {
    return axiosInstance
      .get<APIResponse<{ id: number; name: PaymentStatus }[]>>(`${config.domains.members}/members/payment_status_types`)
      .then(({ data }) => data?.data);
  }
};
export const useGetPaymentStatusTypesQuery: any = () =>
  useQuery({
    queryKey: ['payment-status-types'],
    queryFn: getPaymentStatusTypes,
    select(data) {
      if (!Array.isArray(data)) return [];
      return data;
    },
  });

const getRelationshipTypes = () => {
  const config = getConfig();
  if (STATIC_OPTIONS) {
    return import('~/assets/data/api/v1/members/relationship_types.json').then(({ data }: any) => data);
  } else {
    return axiosInstance
      .get<APIResponse<{ id: number; name: string }[]>>(`${config.domains.members}/members/relationship_types`)
      .then(({ data }) => data?.data);
  }
};
export const useGetRelationshipTypesQuery: any = () =>
  useQuery({
    queryKey: ['relationship-types'],
    queryFn: getRelationshipTypes,
    select(data) {
      if (!Array.isArray(data)) return [];
      return data;
    },
  });

const getRelationshipStatusTypes = () => {
  const config = getConfig();
  if (STATIC_OPTIONS) {
    return import('~/assets/data/api/v1/members/relationship_status_types.json').then(({ data }: any) => data);
  } else {
    return axiosInstance
      .get<APIResponse<{ id: number; name: string }[]>>(`${config.domains.members}/members/relationship_status_types`)
      .then(({ data }) => data?.data);
  }
};
export const useGetRelationshipStatusTypesQuery: any = () =>
  useQuery({
    queryKey: ['relationship-status-types'],
    queryFn: getRelationshipStatusTypes,
    select(data) {
      if (!Array.isArray(data)) return [];
      return data;
    },
  });

const getRoleTypes = () => {
  const config = getConfig();

  if (STATIC_OPTIONS) {
    return import('~/assets/data/api/v1.1/auth/role_types.json').then(({ data }: { data: Role[] }) => data);
  } else {
    return axiosInstance
      .get<APIResponse<Role[]>>(`${config.domains.auth}/auth/role_types`, {
        headers: { 'Skip-Authorization': 'true' },
      })
      .then(({ data }) => data?.data);
  }
};

export const useGetRoleTypesQuery = () =>
  useQuery({
    queryKey: ['role-types'],
    queryFn: getRoleTypes,
    select(data) {
      if (!Array.isArray(data)) return [];
      return data;
    },
  });

const getShirtFitTypes = () => {
  const config = getConfig();

  if (STATIC_OPTIONS) {
    return import('~/assets/data/api/v1/members/shirt_fit_types.json').then(({ data }: any) => data);
  } else {
    return axiosInstance
      .get<APIResponse<{ id: number; name: string }[]>>(`${config.domains.members}/members/shirt_fit_types`)
      .then(({ data }) => data?.data);
  }
};
export const useGetShirtFitTypesQuery: any = () =>
  useQuery({
    queryKey: ['shirt-fit-types'],
    queryFn: getShirtFitTypes,
    select(data) {
      if (!Array.isArray(data)) return [];
      return data;
    },
  });

const getShirtSizeTypes = () => {
  const config = getConfig();
  if (STATIC_OPTIONS) {
    return import('~/assets/data/api/v1/members/shirt_size_types.json').then(({ data }: any) => data);
  } else {
    return axiosInstance
      .get<APIResponse<{ id: number; name: string }[]>>(`${config.domains.members}/members/shirt_size_types`)
      .then(({ data }) => data?.data);
  }
};
export const useGetShirtSizeTypesQuery: any = () =>
  useQuery({ queryKey: ['shirt-size-types'], queryFn: getShirtSizeTypes });

const getGymTypes = () => {
  const config = getConfig();
  if (STATIC_OPTIONS) {
    return import('~/assets/data/api/v1.1/auth/gym_types.json').then(({ data }: any) => data);
  } else {
    return axiosInstance
      .get<APIResponse<{ id: number; name: GymType }[]>>(`${config.domains.auth}/auth/gym_types`)
      .then(({ data }) => data?.data);
  }
};
export const useGetGymTypesQuery: any = () =>
  useQuery({
    queryKey: ['gym-types'],
    queryFn: getGymTypes,
    select(data) {
      if (!Array.isArray(data)) return [];
      return data;
    },
  });

const getLocationTypes = () => {
  const config = getConfig();
  if (STATIC_OPTIONS) {
    return import('~/assets/data/api/v1.1/auth/location_types.json').then(({ data }: any) => data);
  } else {
    return axiosInstance
      .get<APIResponse<{ id: number; name: LocationType }[]>>(`${config.domains.auth}/auth/location_types`)
      .then(({ data }) => data?.data);
  }
};
export const useGetLocationTypesQuery: any = () =>
  useQuery({
    queryKey: ['location-types'],
    queryFn: getLocationTypes,
    select(data) {
      if (!Array.isArray(data)) return [];
      return data;
    },
  });

const getRegistrationTimeTypes = () => {
  const config = getConfig();
  if (STATIC_OPTIONS) {
    return import('~/assets/data/api/v1.1/auth/registration_time_types.json').then(({ data }: any) => data);
  } else {
    return axiosInstance
      .get<APIResponse<{ id: number; name: string }[]>>(`${config.domains.auth}/auth/registration_time_types`)
      .then(({ data }) => data?.data);
  }
};
export const useGetRegistrationTimeTypesQuery: any = () =>
  useQuery({ queryKey: ['registration-time-types'], queryFn: getRegistrationTimeTypes });

const getClassTypes = () => {
  const config = getConfig();
  if (STATIC_OPTIONS) {
    return import('~/assets/data/api/v1/classes/class_types.json').then(({ data }: any) => data);
  } else {
    return axiosInstance
      .get<APIResponse<{ id: number; name: string }[]>>(`${config.domains.plans}/classes/class_types`)
      .then(({ data }) => data?.data);
  }
};
export const useGetClassTypesQuery: any = () =>
  useQuery({
    queryKey: ['class-types'],
    queryFn: getClassTypes,
    select(data) {
      if (!Array.isArray(data)) return [];
      return data;
    },
  });

const getTimezoneTypes = () => {
  const config = getConfig();
  if (STATIC_OPTIONS) {
    return import('~/assets/data/api/v1.1/auth/timezone_types.json').then(({ data }: any) => data);
  } else {
    return axiosInstance
      .get<APIResponse<{ id: number; name: string }[]>>(`${config.domains.auth}/auth/timezone_types`)
      .then(({ data }) => data?.data);
  }
};
export const useGetTimezoneTypesQuery: any = () =>
  useQuery({ queryKey: ['timezone-types'], queryFn: getTimezoneTypes });

const getCancelReasonTypes = () => {
  const config = getConfig();
  if (STATIC_OPTIONS) {
    return import('~/assets/data/api/v1/members/cancel_reason_types.json').then(({ data }: any) => data);
  } else {
    return axiosInstance
      .get<APIResponse<{ id: number; name: string }[]>>(`${config.domains.members}/members/cancel_reason_types`)
      .then(({ data }) => data?.data);
  }
};
export const useGetCancelReasonTypesQuery: any = () =>
  useQuery({
    queryKey: ['cancel-reason-types'],
    queryFn: getCancelReasonTypes,
    select(data) {
      if (!Array.isArray(data)) return [];
      return data;
    },
  });

const getFreezeReasonTypes = () => {
  const config = getConfig();
  if (STATIC_OPTIONS) {
    return import('~/assets/data/api/v1/members/freeze_reason_types.json').then(
      ({ data }: { data: { id: number; name: string }[] }) => data,
    );
  } else {
    return axiosInstance
      .get<APIResponse<{ id: number; name: string }[]>>(`${config.domains.members}/members/freeze_reason_types`)
      .then(({ data }) => data?.data);
  }
};
export const useGetFreezeReasonTypesQuery = () =>
  useQuery({
    queryKey: ['freeze-reason-types'],
    queryFn: getFreezeReasonTypes,
  });

const getMembershipTypes = () => {
  const config = getConfig();
  if (STATIC_OPTIONS) {
    return import('~/assets/data/api/v1/plans/membership_types.json').then(({ data }: any) => data);
  } else {
    return axiosInstance
      .get<APIResponse<{ id: number; name: string }[]>>(`${config.domains.plans}/plans/membership_types`)
      .then(({ data }) => data?.data);
  }
};
export const useGetMembershipTypesQuery: any = () =>
  useQuery({
    queryKey: ['membership-types'],
    queryFn: getMembershipTypes,
    select(data) {
      if (!Array.isArray(data)) return [];
      return data;
    },
  });
